import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import api from '@/lib/api';
import { ArrowLeft, Plus, Trash2, Save, FileCheck, Lock, Loader2 } from 'lucide-react';
import { formatName } from '@/lib/utils';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';

export default function InvoiceFormPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const lockedClientId = searchParams.get('client_id') || '';
  const isNew = !id || id === 'new';
  const [clients, setClients] = useState([]);
  const [branches, setBranches] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [packages, setPackages] = useState([]);
  const [taxSettings, setTaxSettings] = useState({ gst_enabled: false, default_payment_terms_days: 7, default_terms: '' });
  const today = new Date().toISOString().split('T')[0];

  const [form, setForm] = useState({
    invoice_type: 'INVOICE',
    client_id: lockedClientId,
    branch_id: '',
    invoice_date: today,
    due_date: '',
    lines: [],
    invoice_discount_type: 'FLAT',
    invoice_discount_value: 0,
    notes_terms: '',
    internal_notes: '',
    is_draft: true,
  });
  const [loading, setLoading] = useState(false);
  const saveBusyRef = useRef(false);

  useEffect(() => {
    const load = async () => {
      const [c, b, cat, pkg, ts] = await Promise.all([
        api.get('/clients'), api.get('/branches'),
        api.get('/catalog?is_active=true'), api.get('/packages?is_active=true'),
        api.get('/tax-settings'),
      ]);
      setClients(c.data || []);
      setBranches(b.data || []);
      setCatalog(cat.data || []);
      setPackages(pkg.data || []);
      setTaxSettings(ts.data);
      // Set defaults from settings
      setForm(prev => ({
        ...prev,
        notes_terms: prev.notes_terms || ts.data?.default_terms || '',
      }));
      if (!isNew) {
        const inv = await api.get(`/invoices/${id}`);
        setForm({
          invoice_type: inv.data.invoice_type || 'INVOICE',
          client_id: inv.data.client_id,
          branch_id: inv.data.branch_id || '',
          invoice_date: inv.data.invoice_date,
          due_date: inv.data.due_date || '',
          lines: inv.data.lines || [],
          invoice_discount_type: inv.data.invoice_discount_type || 'FLAT',
          invoice_discount_value: inv.data.invoice_discount_value || 0,
          notes_terms: inv.data.notes_terms || '',
          internal_notes: inv.data.internal_notes || '',
          is_draft: inv.data.is_draft !== false,
        });
      } else if (ts.data?.default_payment_terms_days) {
        const due = new Date(); due.setDate(due.getDate() + ts.data.default_payment_terms_days);
        setForm(prev => ({ ...prev, due_date: due.toISOString().split('T')[0] }));
      }
    };
    load();
  }, [id, isNew]);

  // Auto-pick branch from primary therapist when client changes
  useEffect(() => {
    if (form.client_id && !form.branch_id) {
      const client = clients.find(c => c._id === form.client_id);
      if (client?.primary_therapist_id) {
        // Branch resolution requires therapist data; try basic
        // We let backend resolve if branch_id stays empty.
      }
    }
  }, [form.client_id, clients, form.branch_id]);

  const addCatalogLine = (catId) => {
    const c = catalog.find(x => x._id === catId); if (!c) return;
    setForm(prev => ({ ...prev, lines: [...prev.lines, {
      item_type: c.item_type, catalog_id: c._id, name: c.name,
      code: c.code || '', description: c.description || '',
      quantity: 1, unit_rate: c.unit_rate, line_discount_type: 'FLAT',
      line_discount_value: 0, gst_rate: c.gst_rate || 0,
    }]}));
  };
  const addPackageLine = (pkgId) => {
    const p = packages.find(x => x._id === pkgId); if (!p) return;
    setForm(prev => ({ ...prev, lines: [...prev.lines, {
      item_type: 'PACKAGE', catalog_id: p._id, name: p.name,
      code: '', description: p.description || '',
      quantity: 1, unit_rate: p.selling_price || 0, line_discount_type: 'FLAT',
      line_discount_value: 0, gst_rate: 0, package_items: p.items,
    }]}));
  };
  const updateLine = (i, patch) => setForm(prev => ({ ...prev, lines: prev.lines.map((l, idx) => idx === i ? { ...l, ...patch } : l) }));
  const removeLine = (i) => setForm(prev => ({ ...prev, lines: prev.lines.filter((_, idx) => idx !== i) }));

  const totals = useMemo(() => {
    let subtotal = 0, lineDisc = 0, taxable = 0, gstSum = 0;
    const cgstByRate = {}, sgstByRate = {};
    const gstEnabled = !!taxSettings.gst_enabled;
    form.lines.forEach(l => {
      const gross = (l.quantity || 0) * (l.unit_rate || 0);
      const dval = l.line_discount_value || 0;
      const ld = l.line_discount_type === 'PCT' ? gross * dval / 100 : dval;
      const tax = Math.max(0, gross - ld);
      const gst = gstEnabled ? tax * (l.gst_rate || 0) / 100 : 0;
      subtotal += gross; lineDisc += ld; taxable += tax; gstSum += gst;
      if (gstEnabled && l.gst_rate > 0) {
        const half = (l.gst_rate || 0) / 2;
        cgstByRate[half] = (cgstByRate[half] || 0) + gst / 2;
        sgstByRate[half] = (sgstByRate[half] || 0) + gst / 2;
      }
    });
    const invDiscVal = form.invoice_discount_value || 0;
    const invDisc = form.invoice_discount_type === 'PCT' ? taxable * invDiscVal / 100 : invDiscVal;
    const taxableAfter = Math.max(0, taxable - invDisc);
    const scale = taxable > 0 ? taxableAfter / taxable : 1;
    const cgstScaled = {}, sgstScaled = {};
    let cgstTotal = 0, sgstTotal = 0;
    Object.entries(cgstByRate).forEach(([k, v]) => { cgstScaled[k] = v * scale; cgstTotal += v * scale; });
    Object.entries(sgstByRate).forEach(([k, v]) => { sgstScaled[k] = v * scale; sgstTotal += v * scale; });
    const pre = taxableAfter + cgstTotal + sgstTotal;
    const grand = Math.round(pre);
    const roundOff = grand - pre;
    return { subtotal, lineDisc, invDisc, taxableAfter, cgstScaled, sgstScaled, cgstTotal, sgstTotal, roundOff, grand, gstEnabled };
  }, [form.lines, form.invoice_discount_type, form.invoice_discount_value, taxSettings]);

  const save = async (finalize) => {
    if (saveBusyRef.current) return; // synchronous guard against double-submit
    if (!form.client_id) { alert('Select client'); return; }
    if (form.lines.length === 0) { alert('Add at least one line item'); return; }
    saveBusyRef.current = true;
    setLoading(true);
    try {
      const payload = { ...form, is_draft: !finalize };
      if (isNew) {
        const r = await api.post('/invoices', payload);
        toast.success(finalize ? 'Invoice finalized successfully.' : 'Invoice saved as draft.');
        navigate(`/invoicing/invoice/${r.data._id}`);
      } else {
        await api.put(`/invoices/${id}`, payload);
        if (finalize) await api.post(`/invoices/${id}/finalize`);
        toast.success(finalize ? 'Invoice finalized successfully.' : 'Invoice saved successfully.');
        navigate(`/invoicing/invoice/${id}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed. Please try again.');
      saveBusyRef.current = false;
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="invoice-form-page">
      <div className="flex items-center justify-between">
        <Button variant="ghost" onClick={() => navigate('/invoicing')} className="text-[#4A6B68]"><ArrowLeft className="w-4 h-4 mr-2" /> Back</Button>
        <h1 className="text-xl font-semibold text-[#1A3330] font-['Outfit']">{isNew ? 'Create' : 'Edit'} Invoice</h1>
        <div />
      </div>

      <Card className="bg-white border-[#E5F2F1] rounded-2xl">
        <CardContent className="p-5 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <div>
              <Label>Type</Label>
              <Select value={form.invoice_type} onValueChange={v => setForm({...form, invoice_type: v})}>
                <SelectTrigger className="rounded-xl border-[#E5F2F1]" data-testid="inv-type"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="INVOICE">Invoice</SelectItem><SelectItem value="PROFORMA">Proforma</SelectItem></SelectContent>
              </Select>
            </div>
            <div>
              <Label>Client *</Label>
              {lockedClientId ? (
                <div className="flex items-center gap-2 h-10 px-3 rounded-xl border border-[#E5F2F1] bg-[#F4FAF9]" data-testid="inv-client-locked">
                  <Lock className="w-3.5 h-3.5 text-[#4A6B68]" />
                  <span className="text-sm text-[#1A3330] font-medium">{formatName(clients.find(c => c._id === lockedClientId) || {})}</span>
                </div>
              ) : (
                <Select value={form.client_id} onValueChange={v => setForm({...form, client_id: v})}>
                  <SelectTrigger className="rounded-xl border-[#E5F2F1]" data-testid="inv-client"><SelectValue placeholder="Select..." /></SelectTrigger>
                  <SelectContent>{clients.map(c => <SelectItem key={c._id} value={c._id}>{formatName(c)}</SelectItem>)}</SelectContent>
                </Select>
              )}
            </div>
            <div>
              <Label>Branch</Label>
              <Select value={form.branch_id || 'auto'} onValueChange={v => setForm({...form, branch_id: v === 'auto' ? '' : v})}>
                <SelectTrigger className="rounded-xl border-[#E5F2F1]" data-testid="inv-branch"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="auto">Auto (from therapist)</SelectItem>{branches.map(b => <SelectItem key={b._id} value={b._id}>{b.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Invoice Date</Label>
              <Input type="date" value={form.invoice_date} onChange={e => setForm({...form, invoice_date: e.target.value})} className="rounded-xl border-[#E5F2F1]" />
            </div>
            <div>
              <Label>Due Date</Label>
              <Input type="date" value={form.due_date} onChange={e => setForm({...form, due_date: e.target.value})} className="rounded-xl border-[#E5F2F1]" />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-white border-[#E5F2F1] rounded-2xl">
        <CardContent className="p-5 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-[#1A3330] font-['Outfit']">Line Items</h3>
            <div className="flex gap-2">
              <Select onValueChange={v => addCatalogLine(v)}>
                <SelectTrigger className="rounded-xl border-[#E5F2F1] w-44 h-9 text-sm" data-testid="add-service-product"><SelectValue placeholder="+ Service / Product" /></SelectTrigger>
                <SelectContent>{catalog.map(c => <SelectItem key={c._id} value={c._id}>{c.name} ({c.item_type === 'SERVICE' ? 'S' : 'P'}) Rs.{c.unit_rate}</SelectItem>)}</SelectContent>
              </Select>
              <Select onValueChange={v => addPackageLine(v)}>
                <SelectTrigger className="rounded-xl border-[#E5F2F1] w-36 h-9 text-sm" data-testid="add-package"><SelectValue placeholder="+ Package" /></SelectTrigger>
                <SelectContent>{packages.map(p => <SelectItem key={p._id} value={p._id}>{p.name} Rs.{p.selling_price}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          {form.lines.length === 0 ? <p className="text-sm text-[#4A6B68] text-center py-6">No line items. Use buttons above to add.</p> : (
            <div className="border border-[#E5F2F1] rounded-xl divide-y divide-[#E5F2F1]">
              {form.lines.map((l, i) => {
                const gross = (l.quantity || 0) * (l.unit_rate || 0);
                const dval = l.line_discount_value || 0;
                const ld = l.line_discount_type === 'PCT' ? gross * dval / 100 : dval;
                const taxable = Math.max(0, gross - ld);
                const gst = totals.gstEnabled ? taxable * (l.gst_rate || 0) / 100 : 0;
                return (
                  <div key={i} className="p-3 grid grid-cols-12 gap-2 items-center" data-testid={`inv-line-${i}`}>
                    <div className="col-span-12 sm:col-span-3 text-sm">
                      <p className="font-medium text-[#1A3330]">{l.name} <Badge className="text-[9px] border-0 bg-[#F4FAF9] text-[#4A6B68]">{l.item_type}</Badge></p>
                      <Input value={l.description || ''} onChange={e => updateLine(i, {description: e.target.value})} placeholder="Description" className="h-7 mt-1 text-xs rounded-lg" />
                    </div>
                    <div className="col-span-6 sm:col-span-1"><Label className="text-[10px]">Qty</Label><Input type="number" value={l.quantity} onChange={e => updateLine(i, {quantity: parseFloat(e.target.value || 0)})} className="h-8 text-sm rounded-lg" /></div>
                    <div className="col-span-6 sm:col-span-2"><Label className="text-[10px]">Rate</Label><Input type="number" step="0.01" value={l.unit_rate} onChange={e => updateLine(i, {unit_rate: parseFloat(e.target.value || 0)})} className="h-8 text-sm rounded-lg" /></div>
                    <div className="col-span-6 sm:col-span-2">
                      <Label className="text-[10px]">Discount</Label>
                      <div className="flex gap-1">
                        <Select value={l.line_discount_type} onValueChange={v => updateLine(i, {line_discount_type: v})}>
                          <SelectTrigger className="h-8 text-xs rounded-lg w-16"><SelectValue /></SelectTrigger>
                          <SelectContent><SelectItem value="FLAT">₹</SelectItem><SelectItem value="PCT">%</SelectItem></SelectContent>
                        </Select>
                        <Input type="number" value={l.line_discount_value} onChange={e => updateLine(i, {line_discount_value: parseFloat(e.target.value || 0)})} className="h-8 text-sm rounded-lg" />
                      </div>
                    </div>
                    {totals.gstEnabled && (
                      <div className="col-span-6 sm:col-span-1"><Label className="text-[10px]">GST%</Label>
                        <Select value={String(l.gst_rate || 0)} onValueChange={v => updateLine(i, {gst_rate: parseFloat(v)})}>
                          <SelectTrigger className="h-8 text-xs rounded-lg"><SelectValue /></SelectTrigger>
                          <SelectContent>{[0,5,12,18,28].map(g => <SelectItem key={g} value={String(g)}>{g}%</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                    )}
                    <div className={`col-span-${totals.gstEnabled ? '2' : '3'} sm:col-span-${totals.gstEnabled ? '2' : '3'} text-right`}>
                      <Label className="text-[10px]">Total</Label>
                      <p className="text-sm font-semibold text-[#1A3330]">Rs.{(taxable + gst).toLocaleString()}</p>
                    </div>
                    <div className="col-span-1"><Button type="button" variant="ghost" size="sm" onClick={() => removeLine(i)} data-testid={`remove-line-${i}`}><Trash2 className="w-3.5 h-3.5 text-[#D3544E]" /></Button></div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="bg-white border-[#E5F2F1] rounded-2xl">
        <CardContent className="p-5 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-3">
            <div>
              <Label>Invoice-Level Discount</Label>
              <div className="flex gap-2">
                <Select value={form.invoice_discount_type} onValueChange={v => setForm({...form, invoice_discount_type: v})}>
                  <SelectTrigger className="rounded-xl border-[#E5F2F1] w-20"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="FLAT">₹</SelectItem><SelectItem value="PCT">%</SelectItem></SelectContent>
                </Select>
                <Input type="number" value={form.invoice_discount_value} onChange={e => setForm({...form, invoice_discount_value: parseFloat(e.target.value || 0)})} className="rounded-xl border-[#E5F2F1]" data-testid="inv-discount" />
              </div>
            </div>
            <div><Label>Terms & Conditions</Label><Textarea rows={4} value={form.notes_terms} onChange={e => setForm({...form, notes_terms: e.target.value})} className="rounded-xl border-[#E5F2F1]" data-testid="inv-terms" /></div>
            <div><Label>Internal Notes (not on PDF)</Label><Textarea rows={2} value={form.internal_notes} onChange={e => setForm({...form, internal_notes: e.target.value})} className="rounded-xl border-[#E5F2F1]" data-testid="inv-internal" /></div>
          </div>
          <div className="bg-[#F4FAF9] rounded-2xl p-4 space-y-2 text-sm">
            <Row label="Subtotal" value={`Rs.${totals.subtotal.toLocaleString()}`} />
            {totals.lineDisc > 0 && <Row label="Line Discounts" value={`- Rs.${totals.lineDisc.toLocaleString()}`} />}
            {totals.invDisc > 0 && <Row label="Invoice Discount" value={`- Rs.${totals.invDisc.toLocaleString()}`} />}
            {totals.gstEnabled && <Row label="Taxable Amount" value={`Rs.${totals.taxableAfter.toLocaleString()}`} />}
            {totals.gstEnabled && Object.entries(totals.cgstScaled).map(([r, v]) => <Row key={'c'+r} label={`CGST @${r}%`} value={`Rs.${v.toFixed(2)}`} />)}
            {totals.gstEnabled && Object.entries(totals.sgstScaled).map(([r, v]) => <Row key={'s'+r} label={`SGST @${r}%`} value={`Rs.${v.toFixed(2)}`} />)}
            {Math.abs(totals.roundOff) > 0.001 && <Row label="Round Off" value={`Rs.${totals.roundOff.toFixed(2)}`} />}
            <div className="pt-2 mt-2 border-t border-[#0D8B7D]/20 flex items-baseline justify-between">
              <span className="text-base font-bold text-[#0D8B7D]">Grand Total</span>
              <span className="text-2xl font-bold text-[#0D8B7D]" data-testid="inv-grand-total">Rs.{totals.grand.toLocaleString()}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={() => save(false)} disabled={loading} className="rounded-xl border-[#E5F2F1]" data-testid="save-draft-btn">{loading ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</>) : (<><Save className="w-4 h-4 mr-2" /> Save as Draft</>)}</Button>
        <Button onClick={() => save(true)} disabled={loading} className="bg-[#0D8B7D] hover:bg-[#0B786C] text-white rounded-xl" data-testid="finalize-btn">{loading ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processing...</>) : (<><FileCheck className="w-4 h-4 mr-2" /> Save & Finalize</>)}</Button>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return <div className="flex justify-between"><span className="text-[#4A6B68]">{label}</span><span className="font-medium text-[#1A3330]">{value}</span></div>;
}
